<script>
	import { createEventDispatcher } from "svelte";
	import { Button, Table } from "@sveltestrap/sveltestrap";
	import CustomTableStatusDropdown from "../../../components/CustomTable/CustomTableStatusDropdown.svelte";
	import CancelWorkOrderModal from "./CancelWorkOrderModal.svelte";
	import { convert } from "src/services/helpers";
	import {
		assignedToLabel,
		jobStatusLabel,
		jobStatusVariantByLabel,
		priorityLabel,
	} from "./jobDisplay";

	export let jobs = [];
	export let readOnly = false;

	const dispatch = createEventDispatcher();

	let isCancelModalOpen = false;
	let jobPendingCancel = null;

	function handleJobStatusSelect(job, statusLabel) {
		if (statusLabel === "Cancelled") {
			jobPendingCancel = job;
			isCancelModalOpen = true;
			return;
		}
		dispatch("statusSelect", { job, statusLabel });
	}

	function closeCancelModal() {
		isCancelModalOpen = false;
		jobPendingCancel = null;
	}

	function confirmCancelJob({ detail: { reasonCode, notes } }) {
		dispatch("statusSelect", {
			job: jobPendingCancel,
			statusLabel: "Cancelled",
			reasonCode,
			notes,
		});
		closeCancelModal();
	}

	// Mirrors JobConfirmationModal.svelte's getJobReasons() — real shape is
	// `GET /v1/Jobs/lookups` → `reasonLookups.deletionReasons`, each
	// `{ id, text }`, not M&R's own cancel-reason catalog. Stand-in until
	// that endpoint is wired in here.
	async function loadJobCancelReasonOptions() {
		const deletionReasons = [
			{ id: "DuplicateJob", text: "Duplicate job" },
			{ id: "WrongVehicle", text: "Created against the wrong vehicle" },
			{ id: "NoLongerNeeded", text: "No longer needed" },
			{ id: "Other", text: "Other" },
		];
		return deletionReasons.map((reason) => ({
			value: reason.id,
			label: reason.text,
		}));
	}

	function toJobStatusDisplayRow(job) {
		return {
			...job,
			status: jobStatusLabel(job.status),
			allowedTransitions: readOnly
				? []
				: (job.allowedTransitions || []).map(jobStatusLabel),
		};
	}

	// Each date column keeps its own direction (like the issues table's
	// toggle); rows follow whichever column was clicked last.
	let sortKey = "start";
	let sortDirections = { start: "descending", due: "descending" };

	function toggleDateSort(key) {
		sortKey = key;
		sortDirections[key] =
			sortDirections[key] === "ascending" ? "descending" : "ascending";
	}

	$: sortDirection = sortDirections[sortKey];

	// Jobs without a date always sink to the bottom, whichever the direction.
	$: sortedJobs = [...jobs].sort((a, b) => {
		const aTime = a[sortKey] ? new Date(a[sortKey]).getTime() : null;
		const bTime = b[sortKey] ? new Date(b[sortKey]).getTime() : null;
		if (aTime === null || bTime === null) {
			return (aTime === null ? 1 : 0) - (bTime === null ? 1 : 0);
		}
		const result = aTime - bTime;
		return sortDirection === "ascending" ? result : -result;
	});
</script>

<div class="d-flex align-items-center mb-3">
	<h5 class="fw-bold mb-0">Jobs</h5>
	<Button
		class="themeButton primary ms-auto px-5"
		disabled={readOnly}
		title={readOnly
			? "This work order is cancelled or completed, so jobs can no longer be added to it."
			: undefined}
		on:click={() => dispatch("addJob")}
	>
		Add job
	</Button>
</div>
<Table responsive hover>
	<thead>
		<tr>
			<th>ID</th>
			<th>Number of tasks</th>
			<!-- Literal-only aria-sort values, as in WorkOrderIssuesTable, so
			     Sonar can verify they're valid tokens. -->
			<th
				aria-sort={sortDirections.start === "ascending"
					? "ascending"
					: "descending"}
			>
				<button
					type="button"
					class="sort-toggle d-flex justify-content-between align-items-center w-100"
					on:click={() => toggleDateSort("start")}
				>
					Start date and time
					{#if sortDirections.start === "ascending"}
						<img src="/assets/icon-sort-down-thin.svg" alt="" />
					{:else}
						<img src="/assets/icon-sort-up-thin.svg" alt="" />
					{/if}
				</button>
			</th>
			<th
				aria-sort={sortDirections.due === "ascending"
					? "ascending"
					: "descending"}
			>
				<button
					type="button"
					class="sort-toggle d-flex justify-content-between align-items-center w-100"
					on:click={() => toggleDateSort("due")}
				>
					End date and time
					{#if sortDirections.due === "ascending"}
						<img src="/assets/icon-sort-down-thin.svg" alt="" />
					{:else}
						<img src="/assets/icon-sort-up-thin.svg" alt="" />
					{/if}
				</button>
			</th>
			<th>Job status</th>
			<th>Priority</th>
			<th>Assigned to</th>
			<th>Added by</th>
		</tr>
	</thead>
	<tbody>
		{#if jobs.length === 0}
			<tr>
				<td colspan="8">
					<b class="d-flex justify-content-center"> No jobs added </b>
				</td>
			</tr>
		{:else}
			{#each sortedJobs as job}
				<tr>
					<td>
						<button
							type="button"
							class="link link-text job-code-link"
							on:click={() => dispatch("openDetail", job)}
						>
							{job.code}
						</button>
					</td>
					<td>{job.taskCount}</td>
					<td>{convert(job.start)}</td>
					<td>{convert(job.due)}</td>
					<td>
						<CustomTableStatusDropdown
							row={toJobStatusDisplayRow(job)}
							key="status"
							options={jobStatusVariantByLabel}
							event={(_row, statusLabel) =>
								handleJobStatusSelect(job, statusLabel)}
						/>
					</td>
					<td>{priorityLabel(job.priority)}</td>
					<td>{assignedToLabel(job.assignedTo)}</td>
					<td>{job.createdBy || "-"}</td>
				</tr>
			{/each}
		{/if}
	</tbody>
</Table>

<CancelWorkOrderModal
	isOpen={isCancelModalOpen}
	subject="job"
	loadReasonOptions={loadJobCancelReasonOptions}
	on:close={closeCancelModal}
	on:confirm={confirmCancelJob}
/>

<style>
	.job-code-link {
		border: none;
		background: none;
		padding: 0;
	}

	.sort-toggle {
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
		font: inherit;
		color: inherit;
		text-align: left;
	}
</style>
